'use client';

import { useIsMobile } from '@/hooks/use-mobile';
import { Input } from '@/components/ui/input/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select/select';
import { useTenantSlug } from '@/hooks/use-tenant-slug';

const CURRENT_YEAR = new Date().getFullYear();
const MIN_YEAR = 1900;

const YEAR_OPTIONS: string[] = (() => {
  const years: string[] = [];
  for (let year = CURRENT_YEAR; year >= MIN_YEAR; year--) {
    years.push(year.toString());
  }
  return years;
})();

interface YearPickerProps {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Class name for the Select dropdown content */
  contentClassName?: string;
}

/**
 * Year picker that adapts to device type.
 *
 * - Desktop: Select dropdown with year options
 * - Mobile: Numeric text input (brings up number pad)
 *
 * Value is always a string for compatibility with form state.
 */
export function YearPicker({
  value,
  onValueChange,
  placeholder = 'e.g. 1975',
  disabled = false,
  className,
  contentClassName,
}: YearPickerProps) {
  const isMobile = useIsMobile();
  const tenantSlug = useTenantSlug();
  const themeLightAttr = tenantSlug === 'dgd' ? 'true' : undefined;

  if (isMobile) {
    return (
      <Input
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={4}
        value={value}
        onChange={(e) => {
          const raw = e.target.value.replace(/\D/g, '').slice(0, 4);
          onValueChange(raw);
        }}
        onBlur={() => {
          if (!value) return;
          const num = parseInt(value, 10);
          if (Number.isNaN(num) || num < MIN_YEAR) {
            onValueChange(MIN_YEAR.toString());
          } else if (num > CURRENT_YEAR) {
            onValueChange(CURRENT_YEAR.toString());
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
      />
    );
  }

  return (
    <Select onValueChange={onValueChange} value={value} disabled={disabled}>
      <SelectTrigger className={className}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent data-theme-light={themeLightAttr} className={contentClassName}>
        {YEAR_OPTIONS.map((year) => (
          <SelectItem key={year} value={year}>
            {year}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
